import { Route } from 'vue-router'
import seoRoutesData from './seo-routes.json'

interface SiteConfig {
  name: string;
  url: string;
  defaultImage: string;
  defaultTitle: string;
  defaultDescription: string;
}

interface SeoRouteEntry {
  name?: string;
  path: string;
  title: string;
  description: string;
  heading: string;
  summary: string;
  changefreq?: string;
  priority?: string;
  schemaType?: string;
  robots?: string;
  includeInSitemap?: boolean;
}

interface GenreEntry {
  name: string;
  slug: string;
  priority?: string;
}

interface SeoConfig {
  site: SiteConfig;
  routes: SeoRouteEntry[];
  genres: GenreEntry[];
}

export interface SeoMeta {
  title: string;
  description: string;
  path: string;
  heading?: string;
  summary?: string;
  image?: string;
  imageAlt?: string;
  type?: string;
  schemaType?: string;
  robots?: string;
}

const seoConfig = seoRoutesData as SeoConfig
const configuredSiteUrl = (process.env.VUE_APP_SITE_URL || seoConfig.site.url).replace(/\/+$/, '')

export const siteName = seoConfig.site.name
export const siteUrl = configuredSiteUrl

function normalizeSlug(value: string) {
  return (value || '').replace(/^\/+|\/+$/g, '').toLowerCase()
}

export function normalizePath(path: string) {
  const cleanPath = (path || '/').split('?')[0].split('#')[0].replace(/\/+$/, '')
  return cleanPath || '/'
}

function humanize(value: string) {
  return value
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase())
}

function absoluteAssetUrl(value?: string) {
  if (!value) {
    return `${configuredSiteUrl}${seoConfig.site.defaultImage}`
  }

  if (/^https?:\/\//i.test(value)) {
    return value
  }

  return `${configuredSiteUrl}${value.startsWith('/') ? value : `/${value}`}`
}

export function absoluteUrl(path: string) {
  const normalizedPath = normalizePath(path)
  return `${configuredSiteUrl}${normalizedPath === '/' ? '/' : normalizedPath}`
}

function buildGenreSeoEntry(genre: GenreEntry): SeoRouteEntry {
  return {
    name: 'GenreTracks',
    path: `/genres/tracks/${genre.slug}`,
    title: `${genre.name} Radio | ${siteName}`,
    description: `Stream ${genre.name} radio with independent tracks from Jamendo, Audius, and public Suno datasets on Robopol Music.`,
    heading: `${genre.name} Radio`,
    summary: `Play independent ${genre.name} tracks and discover artists, albums, and fresh radio rotation on Robopol Music.`,
    changefreq: 'weekly',
    priority: genre.priority || '0.7',
    schemaType: 'CollectionPage',
    includeInSitemap: true,
  }
}

export const defaultImage = absoluteAssetUrl(seoConfig.site.defaultImage)
export const staticSeoRoutes = seoConfig.routes
export const genreSeoRoutes = seoConfig.genres.map((genre) => buildGenreSeoEntry(genre))

function getStaticEntry(path: string) {
  const normalizedPath = normalizePath(path)
  return staticSeoRoutes.find((entry) => normalizePath(entry.path) === normalizedPath)
}

function getGenreEntry(slug: string) {
  const normalizedSlug = normalizeSlug(slug)
  return genreSeoRoutes.find((entry) => entry.path === `/genres/tracks/${normalizedSlug}`)
}

function toMeta(entry: SeoRouteEntry): SeoMeta {
  return {
    title: entry.title,
    description: entry.description,
    path: entry.path,
    heading: entry.heading,
    summary: entry.summary,
    schemaType: entry.schemaType,
    robots: entry.robots,
  }
}

export function getSeoForRoute(route: Route): SeoMeta {
  if (route.name === 'GenreTracks') {
    const genreEntry = getGenreEntry(route.params.link)
    if (genreEntry) {
      return toMeta(genreEntry)
    }

    const genreName = humanize(route.params.link || 'Genre')
    return {
      title: `${genreName} Radio | ${siteName}`,
      description: `Stream ${genreName} radio with independent tracks from Robopol Music sources.`,
      path: `/genres/tracks/${normalizeSlug(route.params.link || 'genre')}`,
      heading: `${genreName} Radio`,
      summary: `Play independent ${genreName} tracks and discover new music on Robopol Music.`,
      schemaType: 'CollectionPage',
    }
  }

  if (route.name === 'Artist') {
    return {
      title: `Artist Details | ${siteName}`,
      description: 'Listen to independent artist tracks and albums on Robopol Music.',
      path: route.path,
      heading: 'Artist Details',
      summary: 'Browse artist tracks, albums, and independent music releases.',
      schemaType: 'MusicGroup',
    }
  }

  if (route.name === 'Albums') {
    return {
      title: `Album Details | ${siteName}`,
      description: 'Stream an independent music album, browse its tracks, and download available songs on Robopol Music.',
      path: route.path,
      heading: 'Album Details',
      summary: 'Browse album tracks and stream independent music.',
      schemaType: 'MusicAlbum',
    }
  }

  if (route.name === 'Search') {
    const query = route.params.query || 'Music'
    return {
      title: `Search ${query} | ${siteName}`,
      description: `Search Robopol Music for ${query}.`,
      path: route.path,
      heading: `Search ${query}`,
      summary: 'Search tracks, artists, and albums across Robopol Music sources.',
      schemaType: 'SearchResultsPage',
      robots: 'noindex, follow',
    }
  }

  if (route.name === 'PlayTracks') {
    return {
      title: `Playlist Tracks | ${siteName}`,
      description: 'Open a personal Robopol Music playlist and continue listening.',
      path: route.path,
      heading: 'Playlist Tracks',
      summary: 'Play saved tracks from a personal playlist.',
      schemaType: 'MusicPlaylist',
      robots: 'noindex, follow',
    }
  }

  const staticEntry = getStaticEntry(route.path)
  if (staticEntry) {
    return toMeta(staticEntry)
  }

  return {
    title: seoConfig.site.defaultTitle,
    description: seoConfig.site.defaultDescription,
    path: '/',
    heading: siteName,
    summary: seoConfig.site.defaultDescription,
    schemaType: 'WebApplication',
  }
}

function ensureMetaByName(name: string) {
  let tag = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute('name', name)
    document.head.appendChild(tag)
  }
  return tag
}

function ensureMetaByProperty(property: string) {
  let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute('property', property)
    document.head.appendChild(tag)
  }
  return tag
}

function ensureCanonical() {
  let tag = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null
  if (!tag) {
    tag = document.createElement('link')
    tag.setAttribute('rel', 'canonical')
    document.head.appendChild(tag)
  }
  return tag
}

function buildStructuredData(meta: SeoMeta) {
  const url = absoluteUrl(meta.path)
  const image = absoluteAssetUrl(meta.image)

  if (meta.schemaType === 'WebApplication') {
    return {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: siteName,
      url,
      applicationCategory: 'MultimediaApplication',
      operatingSystem: 'Web',
      description: meta.description,
      image,
      publisher: {
        '@type': 'Organization',
        name: 'Robopol',
      },
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'EUR',
      },
    }
  }

  return {
    '@context': 'https://schema.org',
    '@type': meta.schemaType || 'WebPage',
    name: meta.heading || meta.title,
    headline: meta.title,
    description: meta.description,
    url,
    image,
    isPartOf: {
      '@type': 'WebSite',
      name: siteName,
      url: absoluteUrl('/'),
    },
    publisher: {
      '@type': 'Organization',
      name: 'Robopol',
    },
  }
}

function updateStructuredData(meta: SeoMeta) {
  let tag = document.querySelector('#seo-jsonld') as HTMLScriptElement | null
  if (!tag) {
    tag = document.querySelector('script[type="application/ld+json"]') as HTMLScriptElement | null
  }

  if (!tag) {
    tag = document.createElement('script')
    tag.type = 'application/ld+json'
    document.head.appendChild(tag)
  }

  tag.id = 'seo-jsonld'
  tag.textContent = JSON.stringify(buildStructuredData(meta))
}

export function setSeoMeta(meta: SeoMeta) {
  if (typeof document === 'undefined') {
    return
  }

  const url = absoluteUrl(meta.path)
  const image = absoluteAssetUrl(meta.image)
  const imageAlt = meta.imageAlt || `${meta.heading || siteName} image`

  document.title = meta.title
  ensureMetaByName('description').setAttribute('content', meta.description)
  ensureMetaByName('robots').setAttribute('content', meta.robots || 'index, follow')
  ensureCanonical().setAttribute('href', url)

  ensureMetaByProperty('og:type').setAttribute('content', meta.type || 'website')
  ensureMetaByProperty('og:site_name').setAttribute('content', siteName)
  ensureMetaByProperty('og:title').setAttribute('content', meta.title)
  ensureMetaByProperty('og:description').setAttribute('content', meta.description)
  ensureMetaByProperty('og:url').setAttribute('content', url)
  ensureMetaByProperty('og:image').setAttribute('content', image)
  ensureMetaByProperty('og:image:alt').setAttribute('content', imageAlt)

  ensureMetaByName('twitter:card').setAttribute('content', 'summary_large_image')
  ensureMetaByName('twitter:title').setAttribute('content', meta.title)
  ensureMetaByName('twitter:description').setAttribute('content', meta.description)
  ensureMetaByName('twitter:url').setAttribute('content', url)
  ensureMetaByName('twitter:image').setAttribute('content', image)

  updateStructuredData(meta)
}

export function updateSeoForRoute(route: Route) {
  setSeoMeta(getSeoForRoute(route))
}
